import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface MetricsSectionNavProps {
  categories: string[];
}

function sectionId(cat: string): string {
  return `category-${cat.toLowerCase().replace(/\s+/g, "-")}`;
}

function getScrollContainer(): HTMLElement | null {
  const marked = document.querySelector<HTMLElement>("[data-metrics-scroll-container]");
  if (marked && marked.scrollHeight > marked.clientHeight) return marked;
  const main = document.querySelector<HTMLElement>("main");
  if (main && main.scrollHeight > main.clientHeight) return main;
  return marked ?? main ?? null;
}

export function MetricsSectionNav({ categories }: MetricsSectionNavProps) {
  const [activeSection, setActiveSection] = useState<string>("");
  const navRef = useRef<HTMLElement>(null);
  const observedRef = useRef<Set<string>>(new Set());
  const pillRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const isClickScrolling = useRef(false);

  const sections = categories.map((cat) => ({ id: sectionId(cat), label: cat }));

  useEffect(() => {
    const navEl = navRef.current;
    if (!navEl || sections.length === 0) return;

    const scrollRoot = getScrollContainer();
    const visibleSections = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleSections.add(entry.target.id);
          } else {
            visibleSections.delete(entry.target.id);
          }
        }

        const topmost = sections.find((s) => visibleSections.has(s.id));
        if (topmost) {
          setActiveSection(topmost.id);
        }
      },
      {
        root: scrollRoot,
        rootMargin: "-64px 0px -20% 0px",
        threshold: [0, 0.1, 0.25],
      },
    );

    const handleScroll = () => {
      if (isClickScrolling.current) return;
      const container = scrollRoot || document.documentElement;
      const atBottom =
        Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) < 80;

      if (atBottom && sections.length > 0) {
        for (let i = sections.length - 1; i >= 0; i--) {
          const el = document.getElementById(sections[i].id);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
              setActiveSection(sections[i].id);
              return;
            }
          }
        }
      }
    };

    const scrollTarget = scrollRoot || window;
    scrollTarget.addEventListener("scroll", handleScroll, { passive: true });

    const tryObserve = () => {
      for (const section of sections) {
        if (observedRef.current.has(section.id)) continue;
        const el = document.getElementById(section.id);
        if (el) {
          observer.observe(el);
          observedRef.current.add(section.id);
        }
      }
    };

    tryObserve();

    const mutationObserver = new MutationObserver(() => {
      tryObserve();
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      scrollTarget.removeEventListener("scroll", handleScroll);
      observedRef.current.clear();
      visibleSections.clear();
    };
  }, [sections]);

  useEffect(() => {
    if (activeSection) {
      const pill = pillRefs.current.get(activeSection);
      if (pill) {
        const row = pill.closest("[data-nav-row]");
        if (row) {
          const pillRect = pill.getBoundingClientRect();
          const rowRect = row.getBoundingClientRect();
          const offset =
            pillRect.left - rowRect.left - rowRect.width / 2 + pillRect.width / 2;
          row.scrollBy({ left: offset, behavior: "smooth" });
        }
      }
    }
  }, [activeSection]);

  const handleClick = useCallback(
    (id: string) => {
      const el = document.getElementById(id);
      const navEl = navRef.current;
      if (!el || !navEl) return;

      setActiveSection(id);
      isClickScrolling.current = true;

      const container = getScrollContainer();
      if (container) {
        const navHeight = navEl.offsetHeight || 48;
        const stickyOffset = navHeight + 8;

        const targetRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollTarget =
          container.scrollTop + (targetRect.top - containerRect.top) - stickyOffset;
        container.scrollTo({ top: Math.max(0, scrollTarget), behavior: "smooth" });
      }

      setTimeout(() => {
        isClickScrolling.current = false;
      }, 1200);
    },
    [],
  );

  const setPillRef = useCallback((id: string, el: HTMLButtonElement | null) => {
    if (el) {
      pillRefs.current.set(id, el);
    } else {
      pillRefs.current.delete(id);
    }
  }, []);

  if (sections.length === 0) return null;

  return (
    <nav
      ref={navRef}
      className="sticky top-0 z-40 -mx-1 px-1 py-1.5 mb-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      data-testid="nav-metrics-sections"
    >
      <div className="hidden sm:flex items-center gap-1 overflow-x-auto" data-nav-row="categories">
        {sections.map((section) => {
          const isActive = activeSection === section.id;
          return (
            <Button
              key={section.id}
              ref={(el) => setPillRef(section.id, el)}
              variant="ghost"
              size="sm"
              onClick={() => handleClick(section.id)}
              className={`text-xs whitespace-nowrap toggle-elevate ${isActive ? "toggle-elevated bg-primary/10 text-primary" : ""}`}
              data-testid={`nav-pill-${section.id}`}
            >
              {section.label}
            </Button>
          );
        })}
      </div>

      <div className="flex sm:hidden">
        <Select
          value={activeSection || ""}
          onValueChange={(val) => handleClick(val)}
        >
          <SelectTrigger className="w-full text-xs" data-testid="nav-select-metrics-category">
            <SelectValue placeholder={sections[0]?.label ?? "Section"} />
          </SelectTrigger>
          <SelectContent>
            {sections.map((section) => (
              <SelectItem key={section.id} value={section.id}>
                {section.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </nav>
  );
}
